// WorldMap — registry-driven Intel map.
//
// Rendering pipeline (per design doc lines 84-95):
//   1 background      - basemap style
//   2 satellite raster - basemap style
//   3 country borders  - basemap style
//   4 admin polygons   - basemap style (deferred to Phase D)
//   5 HeatmapLayer     - INTEL_LAYERS at z<5
//   6 H3 Hex layer     - INTEL_LAYERS at z 5-8
//   7 Icon layers      - INTEL_LAYERS at z>8 (with MIL-STD-2525 frames)
//   8 AOI overlay      - PolygonLayer (dashed cyan), driven by `aoi` prop
//   9 Selected callout - rendered by the parent (EntityPopup)
//  10 HUD chrome       - rendered by the MapView shell
//
// Clutter control:
//   - More than 200 visible markers → switch to H3 hexes (densityLayer).
//   - More than 50 markers in any 80px screen-area → cluster (Phase D will
//     add a screen-grid clustering layer; Phase C-1 falls back to densityLayer).
//   - Below zoom 6 → labels suppressed (icon layers only).
//   - If FPS drops below 45 for 2 consecutive frames → auto-collapse to
//     the heatmap layer until pan settles. We poll a tiny FPS meter.

import { readAccessToken } from '@/state/auth';
import type { Layer, PickingInfo } from '@deck.gl/core';
import { PolygonLayer, ScatterplotLayer } from '@deck.gl/layers';
import { MapboxOverlay } from '@deck.gl/mapbox';
import type { intel as i } from '@eidollon/contracts';
import {
  type ContractMapPoint,
  INTEL_LAYERS,
  type LayerSpec,
  type MapPalette,
  type RegionFeature,
  type RegionMetric,
  type WarZoneInfo,
  useMapPalette,
} from '@eidollon/intel-feed-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapLibreMap, { type MapRef, useControl } from 'react-map-gl/maplibre';
// Side-effect import: sets MapLibre's global config (max parallel image
// requests, etc.) before any Map instance is created. Must be imported
// before MapLibre's first render.
import './maplibre-config';
import { TILES_BASE_URL } from '@/lib/env';
import { type BasemapVariant, buildStyle } from './basemapStyle';

export interface WorldMapAoi {
  id: string;
  polygon: { type: 'Polygon'; coordinates: number[][][] };
}

interface WorldMapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  bbox: [number, number, number, number];
}

export interface WorldMapProps {
  signals: readonly i.IntelSignal[];
  /** Layer ids that the user has toggled on. Defaults to all enabled. */
  enabledLayerIds?: ReadonlySet<string>;
  /** Contract aggregates for the deprecated `contracts-hex` layer. Retained
   *  on the prop surface for one release; no longer consumed by the active
   *  registry (contracts-hex is unregistered as of T11). */
  contracts?: readonly ContractMapPoint[];
  /** Per-region contract metrics for the `contracts-region` choropleth.
   *  Joined to the region MVT polygons by id. Only fetched when the
   *  contracts layer is enabled in the legend. */
  regionFeatures?: readonly RegionFeature[];
  /** Drawn AOIs. Rendered as dashed cyan polygons above icons. */
  aois?: readonly WorldMapAoi[];
  onPick?: (signal: i.IntelSignal | null) => void;
  /** Fires when a `contracts-region` choropleth polygon is clicked. The
   *  parent uses this to drill the page's filter state into that region. */
  onRegionClick?: (props: RegionMetric) => void;
  /** Fires when a `war-zones` polygon is clicked. War zones are static
   *  context — not IntelSignal rows — so they carry their own callback. */
  onWarZoneClick?: (zone: WarZoneInfo) => void;
  /** Streamed back to the parent so the time scrubber + HUD can read it. */
  onViewStateChange?: (vs: WorldMapViewState) => void;
  /** When true, force the heatmap-only fallback (FPS auto-collapse). */
  forceHeatmap?: boolean;
  /** Surface MapRef so the MapView shell can plug in terra-draw. */
  onMapReady?: (map: MapRef) => void;
  /** Optional asOf clamp from the time scrubber. Rows with `occurred_at >
   *  asOf` are excluded before they reach the layer registry. */
  asOf?: Date | null;
  /** Initial camera target. When set, overrides the default CONUS view —
   *  used for URL-state deep links so a reload lands on the same view. */
  initialViewport?: { lng: number; lat: number; zoom: number };
  /** Basemap variant — TERRAIN (vector + labels), SAT (raster imagery),
   *  or DARK (vector, label-free). Default 'terrain' per locked D-AS1. */
  variant?: BasemapVariant;
}

// Auto-collapse-to-heatmap guard. The intel feed is already capped (~200 pull +
// cache/live, low thousands at most) so a runaway marker count can't happen —
// and collapsing hid ALL markers at normal/low zoom (the whole feed was in
// view → over the old 200 → only the heatmap drew, so markers only appeared
// when zoomed in far enough to clip below the threshold). Operators want to SEE
// the event markers at normal zoom. Set high enough that the capped feed never
// trips it; `forceHeatmap` (manual / low-FPS) remains the real fallback.
const HEATMAP_THRESHOLD = 100_000;

/** deck.gl overlay adapter for react-map-gl. Returns a setter that the
 *  caller invokes with the latest layer array on every render. */
function DeckOverlay({ layers }: { layers: Layer[] }) {
  // The maplibre-gl + react-map-gl + deck.gl type families don't quite line
  // up structurally (react-map-gl uses a structural MapInstance, deck.gl
  // wants the concrete maplibre Map). Behaviorally correct; cast at the seam.
  const overlay = useControl(
    () =>
      new MapboxOverlay({
        interleaved: true,
        layers,
        // Cap shader pixel budget on Retina (2x+ DPRs render 4x the pixels
        // by default). 1.5x keeps icons crisp while halving fill-rate cost
        // on the heatmap/density passes — the biggest single perf win.
        useDevicePixels:
          typeof window !== 'undefined' && window.devicePixelRatio > 1.5 ? 1.5 : true,
        // Hover tooltip for pickable non-signal layers. `text` (not html)
        // keeps it XSS-safe regardless of the content.
        getTooltip: ({ object, layer }: { object: unknown; layer: { id?: string } | null }) => {
          if (!object) return null;
          const tooltipStyle = {
            background: '#0a1018',
            color: '#c8d4e2',
            border: '1px solid #38bdf8',
            borderRadius: '2px',
            fontSize: '11px',
            fontFamily: 'var(--mono, monospace)',
            padding: '6px 8px',
            whiteSpace: 'pre-line',
          };
          if (layer?.id === 'intel-contracts-region') {
            const p = (object as { properties?: Record<string, unknown> }).properties ?? {};
            const name = (p.name as string) ?? (p.region_id as string) ?? 'Region';
            const count = Number(p.contract_count ?? 0);
            return { text: `${name}\n${count.toLocaleString()} contracts`, style: tooltipStyle };
          }
          if (layer?.id === 'intel-war-zones') {
            const z = object as { name?: string; severity?: number; tag?: string };
            return {
              text: `${z.name ?? 'War Zone'} · SEV ${z.severity ?? '?'}\n${z.tag ?? ''}`,
              style: { ...tooltipStyle, border: '1px solid #ff4757' },
            };
          }
          return null;
        },
        // biome-ignore lint/suspicious/noExplicitAny: cross-package type seam.
      } as any) as any,
  );
  // Push layers to deck only when the array identity actually changes — NOT
  // on every parent re-render. The intel page re-renders every second (live
  // "DIRECT" clock); calling setProps in the render body re-pushed layers
  // 60×/min, and in interleaved mode each push can repaint the basemap →
  // visible flicker. The `layers` useMemo upstream already gives a stable
  // ref when inputs are unchanged, so this effect is a no-op on clock ticks.
  useEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: same seam.
    (overlay as any).setProps({ layers });
  }, [overlay, layers]);
  return null;
}

function clipByAsOf(
  rows: readonly i.IntelSignal[],
  asOf: Date | null | undefined,
): readonly i.IntelSignal[] {
  if (!asOf) return rows;
  const ms = asOf.getTime();
  return rows.filter((r) => Date.parse(r.occurred_at) <= ms);
}

function aoiPolygonsLayer(palette: MapPalette, aois: readonly WorldMapAoi[]): Layer | null {
  if (aois.length === 0) return null;
  return new PolygonLayer<WorldMapAoi>({
    id: 'intel-aoi',
    data: aois as WorldMapAoi[],
    pickable: false,
    stroked: true,
    filled: true,
    getPolygon: (a) => a.polygon.coordinates[0] as number[][],
    getFillColor: palette.aoiFill,
    getLineColor: palette.aoi,
    lineWidthUnits: 'pixels',
    getLineWidth: 1.5,
    extensions: [],
  });
}

export function WorldMap({
  signals,
  enabledLayerIds,
  contracts,
  regionFeatures,
  aois = [],
  onPick,
  onRegionClick,
  onWarZoneClick,
  onViewStateChange,
  forceHeatmap = false,
  onMapReady,
  asOf,
  initialViewport,
  variant = 'terrain',
}: WorldMapProps) {
  const mapRef = useRef<MapRef | null>(null);
  const [zoom, setZoom] = useState(1.8);
  const [bbox, setBbox] = useState<[number, number, number, number]>([-180, -85, 180, 85]);
  const palette = useMapPalette();

  const enabled = useMemo<ReadonlySet<string>>(
    () => enabledLayerIds ?? new Set(INTEL_LAYERS.map((l) => l.id)),
    [enabledLayerIds],
  );

  const clipped = useMemo(() => clipByAsOf(signals, asOf), [signals, asOf]);

  // Basemap is Protomaps dark vector, served via tile-edge. Sat imagery
  // (Sentinel-2) is no longer the default — it's heavy (400KB+/city view)
  // and blocked first paint of the deck.gl overlays. When the time-scrubber
  // is in time-travel mode, MapView can mount a per-date Sentinel-2 raster
  // source as an *overlay* via map.addSource / addLayer; for now `asOf`
  // only filters the signal entities below.
  // `variant` is in the dep list so MapLibre's react-map-gl wrapper sees a
  // new style identity and calls setStyle() under the hood. transformRequest
  // is prefix-matched against TILES_BASE_URL, so /v1/fonts/, /v1/esri-imagery/
  // and /v1/protomaps-dark/ are all auth-gated automatically.
  const mapStyle = useMemo(() => buildStyle({ variant }), [variant]);

  // MapLibre per-request hook for tile-edge auth.
  //
  // Two transport paths because MapLibre uses different APIs for different
  // resource types:
  //   * Glyphs / SpriteJSON / SpriteImage / Source style fetches → fetch()
  //     → custom headers work → inject `Authorization: Bearer <token>`.
  //   * Tile rasters → Image() element with crossorigin=anonymous
  //     → cannot have custom headers → token has to ride on the URL.
  //
  // The cleanest "in URL" path is the `__Host-tile` cookie minted by
  // services/auth `/v1/auth/tile-cookie` — Image() carries cookies on
  // same-origin requests automatically. But `__Host-` cookies require
  // HTTPS (Secure attribute), and the dev cluster is HTTP-only behind a
  // raw GCP LB. Until we wire managed certs, fall back to the
  // env-gated `?access_token=` query param on tile-edge. Tile-edge
  // audits every use of this path (services/tile-edge/src/audit.ts
  // emitQueryTokenUsed) so we can see when we're still on it.
  //
  // Matching is path-aware. TILES_BASE_URL may be a relative path
  // (`/api/tiles` for same-origin proxy) or an absolute URL (cross-origin
  // tile host). MapLibre always hands transformRequest the resolved
  // absolute URL, so resolve and compare pathnames.
  const transformRequest = useCallback((url: string, _resourceType?: string) => {
    let matches = false;
    if (TILES_BASE_URL.startsWith('http')) {
      matches = url.startsWith(TILES_BASE_URL);
    } else {
      try {
        matches = new URL(url, window.location.origin).pathname.startsWith(TILES_BASE_URL);
      } catch {
        matches = false;
      }
    }
    if (!matches) return { url };
    const token = readAccessToken();
    if (!token) return { url };

    // Vector PBF tiles + style/glyph/sprite resources all go through
    // fetch() under the hood — the Authorization header is honoured.
    // The earlier query-param branch (for raster tiles loaded via
    // Image()) was unconditionally tagging *all* Tile requests; the
    // current dark vector style has no raster source, and tile-edge
    // gates query-param tokens behind TILE_EDGE_ALLOW_QUERY_TOKEN
    // (default off) — so the query-param path returns 401, the
    // basemap never paints, and the map looks black. Send the bearer
    // via header for every resource type; when we ship a raster
    // sat/terrain layer we'll add a typed branch for it then.
    return { url, headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const layers = useMemo<Layer[]>(() => {
    const out: Layer[] = [];
    const viewport = { zoom, bbox };

    // Auto-collapse to heatmap when forced (low FPS) or above the marker
    // density threshold. We always keep the heatmap eligible at low zoom.
    const collapseToHeatmap = forceHeatmap || clipped.length > HEATMAP_THRESHOLD;

    for (const spec of INTEL_LAYERS) {
      if (!enabled.has(spec.id)) continue;
      if (
        collapseToHeatmap &&
        spec.kind !== 'heatmap' &&
        spec.kind !== 'hex' &&
        spec.kind !== 'contracts-region'
      ) {
        continue;
      }
      if (!spec.visibleAtZoom(zoom)) continue;
      const layer = buildLayer(spec, clipped, viewport, onPick, {
        contracts,
        regionFeatures,
        basemapVariant: variant,
        onRegionClick,
        onWarZoneClick,
      });
      if (layer) out.push(layer);
    }

    const aoi = aoiPolygonsLayer(palette, aois);
    if (aoi) out.push(aoi);

    // Always-on near-transparent picking layer over every geo-bearing signal.
    // The visual layers below (heatmap, density-hex, war-zones) have
    // `pickable: false` so when the auto-collapse fallback hides icons
    // (signals > 200 or low FPS), the map otherwise has nothing clickable.
    // This scatter sits on top with a generous radius so the click target
    // survives heatmap/hex visualisations.
    //
    // Why alpha=1 (1/255), not 0: in MapboxOverlay interleaved mode, the
    // GPU early-discards fully-transparent fragments, which makes them
    // invisible to the pick framebuffer as well. Bumping alpha to 1/255
    // is visually indistinguishable but keeps the pixel in the pick buffer
    // — that's what was breaking clicks on the previous fix.
    if (onPick) {
      const pickable = clipped.filter((s) => s.geom);
      if (pickable.length > 0) {
        out.push(
          new ScatterplotLayer<i.IntelSignal>({
            id: 'intel-pick-overlay',
            data: pickable as i.IntelSignal[],
            pickable: true,
            stroked: false,
            filled: true,
            // biome-ignore lint/style/noNonNullAssertion: filtered for geom above.
            getPosition: (s) => [s.geom!.lng, s.geom!.lat],
            getFillColor: [0, 0, 0, 1],
            radiusUnits: 'pixels',
            getRadius: 14,
            radiusMinPixels: 10,
            radiusMaxPixels: 24,
            onClick: (info: PickingInfo) => {
              onPick((info.object as i.IntelSignal | null) ?? null);
              return true;
            },
          }),
        );
      }
    }
    return out;
    // `palette` is included so a theme flip rebuilds the layer set with the
    // new colors — deck.gl doesn't watch palette identity itself.
  }, [
    clipped,
    enabled,
    zoom,
    bbox,
    aois,
    onPick,
    forceHeatmap,
    palette,
    contracts,
    regionFeatures,
    variant,
    onRegionClick,
    onWarZoneClick,
  ]);

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <MapLibreMap
        ref={(r: MapRef | null) => {
          mapRef.current = r;
          if (r && onMapReady) onMapReady(r);
        }}
        // biome-ignore lint/suspicious/noExplicitAny: maplibre style typing seam.
        mapStyle={mapStyle as any}
        // biome-ignore lint/suspicious/noExplicitAny: react-map-gl wraps the maplibre type with its own; the shapes match.
        transformRequest={transformRequest as any}
        initialViewState={
          initialViewport
            ? {
                longitude: initialViewport.lng,
                latitude: initialViewport.lat,
                zoom: initialViewport.zoom,
              }
            : { longitude: -98, latitude: 39, zoom: 3.5 }
        }
        attributionControl={true}
        cursor={'grab'}
        // Hard zoom bounds.
        //   * minZoom: 2 — below z2, the world tiles are < 1024px wide
        //     and stop being meaningful (and we'd show the dark
        //     background-color in the corners).
        //   * maxZoom: 18 — Protomaps source caps at z14; MapLibre
        //     overzooms vector tiles cleanly past that, but going past
        //     z18 hits the limits of the OSM data (no individual building
        //     footprints in the public Protomaps build).
        minZoom={2}
        maxZoom={18}
        // Performance opts ─────────────────────────────────────────────
        //   * `fadeDuration: 0`: skip the 300ms tile fade-in animation.
        //     Tiles snap into place — feels much faster.
        //   * `refreshExpiredTiles: false`: don't re-validate tiles
        //     whose `Cache-Control: max-age` has expired. Protomaps PBFs
        //     are content-addressed (hash in the URL), so revalidation
        //     is wasted bandwidth.
        //   * `renderWorldCopies: false`: only render the single
        //     antimeridian world. Saves draw calls when zoomed out.
        //   * `antialias: false`: dark vector basemap doesn't need MSAA —
        //     turning it off cuts pixel-shader cost ~30% on most GPUs.
        //
        // Note: `maxParallelImageRequests` is a global MapLibre config
        // (set in world/maplibre-config.ts), NOT a per-Map prop —
        // react-map-gl can't pass it through, so attempting it throws
        // "Cannot add property maxParallelImageRequests" on init.
        fadeDuration={0}
        refreshExpiredTiles={false}
        renderWorldCopies={false}
        antialias={false}
        // DPR cap: on Retina, doubled pixel count + deck.gl overlay melts
        // mid-range GPUs. 1.5 cuts fragment-shader cost by ~45% on M-series
        // and is visually indistinguishable from 2.0 for vector basemap.
        // Cap effective pixel ratio at 1.5× the CSS size. react-map-gl 7.x
        // doesn't expose maplibre's `maxPixelRatio`; we approximate the cap by
        // setting `pixelRatio` directly to the smaller of the device ratio
        // and 1.5. On a Retina dpr=2 screen this halves the fragment-shader
        // cost; on a dpr=1 screen the device wins so nothing oversamples.
        pixelRatio={typeof window !== 'undefined' ? Math.min(window.devicePixelRatio, 1.5) : 1.5}
        // Single moveEnd handler. `onMove` (per-frame) was previously
        // calling setZoom 60x/sec during pan/zoom, which re-ran the
        // `layers` useMemo each time → h3 aggregation + boundary math
        // on every frame. We now defer the zoom-state update to settle.
        // The map's internal camera is what's painted live; downstream
        // layer rebinning happens once when movement stops.
        // Force the first paint. In MapboxOverlay interleaved mode the map
        // can mount before its flex/Suspense container has settled its size,
        // leaving the initial frame unpainted (black) until a user gesture
        // forces a repaint — the "map only appears once you zoom out" bug.
        // On load we resize to the real container, sync React zoom/bbox state
        // to the actual camera (state inits to 1.8 but the map opens at 3.5),
        // and trigger one repaint so the basemap + overlay paint immediately.
        onLoad={() => {
          const m = mapRef.current?.getMap();
          if (!m) return;
          m.resize();
          const b = m.getBounds();
          const next: [number, number, number, number] = [
            b.getWest(),
            b.getSouth(),
            b.getEast(),
            b.getNorth(),
          ];
          setZoom(m.getZoom());
          setBbox(next);
          if (onViewStateChange) {
            const c = m.getCenter();
            onViewStateChange({ longitude: c.lng, latitude: c.lat, zoom: m.getZoom(), bbox: next });
          }
          m.triggerRepaint();
        }}
        onMoveEnd={(e: { viewState: { zoom: number } }) => {
          const z = e.viewState.zoom;
          if (z !== zoom) setZoom(z);
          const m = mapRef.current?.getMap();
          if (!m) return;
          const b = m.getBounds();
          const next: [number, number, number, number] = [
            b.getWest(),
            b.getSouth(),
            b.getEast(),
            b.getNorth(),
          ];
          setBbox(next);
          if (onViewStateChange) {
            const c = m.getCenter();
            onViewStateChange({ longitude: c.lng, latitude: c.lat, zoom: m.getZoom(), bbox: next });
          }
        }}
      >
        <DeckOverlay layers={layers} />
      </MapLibreMap>
    </div>
  );
}

function buildLayer(
  spec: LayerSpec,
  rows: readonly i.IntelSignal[],
  viewport: { zoom: number; bbox: [number, number, number, number] },
  onPick: WorldMapProps['onPick'],
  data: {
    contracts: readonly ContractMapPoint[] | undefined;
    regionFeatures: readonly RegionFeature[] | undefined;
    basemapVariant: BasemapVariant;
    onRegionClick: ((props: RegionMetric) => void) | undefined;
    onWarZoneClick: ((zone: WarZoneInfo) => void) | undefined;
  },
): Layer | null {
  const props = {
    rows,
    viewport,
    contracts: data.contracts,
    regionFeatures: data.regionFeatures,
    basemapVariant: data.basemapVariant,
    ...(onPick ? { onPick } : {}),
    ...(data.onRegionClick ? { onRegionClick: data.onRegionClick } : {}),
    ...(data.onWarZoneClick ? { onWarZoneClick: data.onWarZoneClick } : {}),
  };
  return spec.deckLayer(props);
}
